import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { renderDurationAsFormat } from "../../../utils/helpers/date";
import { renderUserName } from "../../../utils/helpers/user";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";

const columnHelper = createColumnHelper();

export const useColumns = ({ edit, remove }) => {
	const { t } = useTranslation();
	const dateFormat = useCompanyDateFormat();
	const timeFormat = useCompanyTimeFormat();

	return useMemo(() => {
		const columns = [
			columnHelper.accessor("startEffectiveDate", {
				header: t("start-effective-date"),
				cell: (info) => {
					const value = info.getValue();
					return value && dateFormat ? moment(value, "YYYY-MM-DD").format(dateFormat) : value ?? "--";
				},
			}),
			columnHelper.accessor("endEffectiveDate", {
				header: t("end-effective-date"),
				cell: (info) => {
					const value = info.getValue();
					return value && dateFormat ? moment(value, "YYYY-MM-DD").format(dateFormat) : value ?? "--";
				},
			}),
			columnHelper.accessor("expectedHours", {
				header: t("expected-hours"),
				cell: (info) => {
					const value = info.getValue();
					if (value == null) return "--";
					return renderDurationAsFormat(Number(value), timeFormat);
				},
			}),
			columnHelper.accessor("expectedWorkingDays", {
				header: t("expected-working-days"),
				cell: (info) => {
					const value = info.getValue();
					if (!Array.isArray(value) || value.length === 0) return "--";
					return value
						.map((day) => t(typeof day === "string" ? day.toLowerCase() : day))
						.join(", ");
				},
			}),
			columnHelper.accessor("expectedWorkingDaysCount", {
				header: t("expected-working-days-count"),
				cell: (info) => {
					const value = info.getValue();
					return value != null ? String(value) : "--";
				},
			}),
			columnHelper.accessor("createdAt", {
				header: t("created-at"),
				cell: (info) => {
					const value = info.getValue();
					return value && dateFormat ? moment(value).format(dateFormat) : value ?? "--";
				},
			}),
			columnHelper.accessor("createdBy", {
				header: t("created-by"),
				cell: (info) => {
					const value = info.getValue();
					return value ? renderUserName(value) : "--";
				},
			}),
			columnHelper.accessor("updatedAt", {
				header: t("updated-at"),
				cell: (info) => {
					const value = info.getValue();
					return value && dateFormat ? moment(value).format(dateFormat) : value ?? "--";
				},
			}),
			columnHelper.accessor("updatedBy", {
				header: t("updated-by"),
				cell: (info) => {
					const value = info.getValue();
					return value ? renderUserName(value) : "--";
				},
			}),
			(!!edit || !!remove) && (
				columnHelper.display({
					id: "actions",
					enableHiding: false,
					enableColumnFilter: false,
					header: (
						<div className="text-right">
							{t("actions")}
						</div>
					),
					cell: (info) => {
						const { id } = info.row.original;
						return (
							<div className="actions text-primary text-right">
								{edit && (
									<span
										className="btn-inner--icon mr-3 icon-sm cursor-pointer"
										onClick={() => edit(id)}
										style={{ cursor: "pointer" }}
									>
										<i className="fas fa-pen" />
									</span>
								)}
								{remove && (
									<span
										className="btn-inner--icon icon-sm cursor-pointer"
										onClick={() => remove(id)}
										style={{ cursor: "pointer" }}
									>
										<i className="fas fa-trash" />
									</span>
								)}
							</div>
						);
					},
				})
			),
		];
		return columns.filter(column => !!column);
	}, [edit, remove, t, dateFormat, timeFormat]);
};
